import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Loader } from '@onramp/components/Loader';
import { PageWrapper } from '@onramp/components/PageWrapper';
import { getRouteWithParams } from '@onramp/hooks/useOnRampRouter';
import { useIsLoggedInEgwSessionManager } from '@onramp/usm/useIsLoggedInEgwSessionManager';
import { useLogWidgetEvent, useLogWidgetEventOnce } from '@onramp/utils/eventing/useLogWidgetEvent';
import { useLogWidgetMetricOnce } from '@onramp/utils/metrics';
import { AnalyticsEventImportance, flushQueue } from '@cbhq/client-analytics';

const messages = defineMessages({
  loading: {
    id: `ActiveSessionProviderV2.loading`,
    defaultMessage: 'Wait for a moment...',
    description: 'Loading message instructing the user to wait.',
  },
  redirecting: {
    id: `ActiveSessionProviderV2.redirecting`,
    defaultMessage: 'Redirecting to sign in...',
    description: 'Loading message indicating that the user will be redirected to login.',
  },
  title: {
    id: `ActiveSessionProviderV2.title`,
    defaultMessage: 'Sign in to Coinbase',
    description: 'Header title while checking if the user is logged in',
  },
});

export const ActiveSessionProviderV2: React.FC = ({ children }) => {
  const { isLoggedIn, isLoading } = useIsLoggedInEgwSessionManager();
  const { formatMessage } = useIntl();
  const logWidgetEventOnce = useLogWidgetEventOnce();
  const logWidgetEvent = useLogWidgetEvent();
  const logWidgetMetricOnce = useLogWidgetMetricOnce();

  useEffect(() => {
    if (isLoading) {
      return;
    }

    if (!isLoggedIn) {
      logWidgetEvent('redirected_to_login', undefined, {
        importance: AnalyticsEventImportance.high,
      });
      logWidgetMetricOnce({
        metricName: 'critical_step',
        value: 1,
        tags: { step: 'redirect-to-login' },
      });

      const currentPath = window.location.pathname;
      const usmSignInRoute = getRouteWithParams('V2Signin');
      if (currentPath !== '') {
        void flushQueue().finally(() => {
          window.location.assign(`${usmSignInRoute}`);
        });
      }
    } else {
      logWidgetEventOnce('has_active_session');
      logWidgetMetricOnce({
        metricName: 'critical_step',
        value: 1,
        tags: { step: 'has-active-session' },
      });
    }
  }, [isLoading, isLoggedIn, logWidgetEvent, logWidgetEventOnce, logWidgetMetricOnce]);

  if (!isLoggedIn) {
    return (
      <PageWrapper headerText={formatMessage(messages.title)}>
        <Loader
          subText={
            isLoading ? formatMessage(messages.loading) : formatMessage(messages.redirecting)
          }
        />
      </PageWrapper>
    );
  }

  return <>{children}</>;
};
